/* eslint-disable @typescript-eslint/await-thenable */

import { createModel, RematchDispatch, RematchRootState } from '@rematch/core';
import { RootModel } from '.';
import { Service, ServicesState, SolutionType } from '../types';
import { ServiceRepository } from '../repositories';
import { ServicesApi } from '../apis';
import { app } from "@microsoft/teams-js";
import * as Constants from '../utils/constants';

const initialservicesState: ServicesState = {
  services: [],
  service: undefined,
  defaultRoute: undefined,
};

export const services = createModel<RootModel>()({
  state: initialservicesState,
  reducers: {
    setServices(state: ServicesState, instances: Service[]): ServicesState {
      return {
        ...state,
        services: instances,
      };
    },
    setService(
      state: ServicesState,
      service: Service | undefined
    ): ServicesState {
      return {
        ...state,
        service,
        defaultRoute: undefined,
      };
    },
    setDefaultRoute(
      state: ServicesState,
      defaultRoute: string | undefined
    ): ServicesState {
      return {
        ...state,
        defaultRoute,
      };
    },
  },
  effects: (dispatch: RematchDispatch<RootModel>) => ({
    async loadServices(
      _,
      rootState: RematchRootState<RootModel>
    ): Promise<void> {
      const instances = await ServiceRepository.getServices(
        rootState.projects.project.solutionType
      );
      this.setServices(instances);
    },

    async loadServiceFromTabData(_, rootState: RematchRootState<RootModel>) {
      const project = rootState.projects.project;
      const tabData = rootState.tab.tabData;
      if (!tabData?.serviceId || !project.name) return;

      const context = await app.getContext();
      const service = await ServicesApi.fetchService(
        tabData.serviceId,
        project.solutionType
      );

      this.setService(service);

      // It's important to use the service ID retrieved from the server.
      // In case project was migrated from CONNECT to ProjectWise 365,
      // the ID will be different from the one that is present in Teams context
      switch (service.id) {
        case Constants.ServiceRegistry.projectWiseWebConnectionsId:
          await dispatch.connections.loadConnectionFromTabData({});
          break;
        case Constants.ServiceRegistry.projectWise365Id:
          await this.loadPw365FromTabData();
          break;
        case Constants.ServiceRegistry.designReviewId:
          await dispatch.imodels.loadIModelFromTabData({});
          break;
        default:
          break;
      }

      await dispatch.tab.loadTabUrls(context.subEntityId ?? '');
    },

    async loadPw365FromTabData(_, rootState: RematchRootState<RootModel>) {
      const service = rootState.services.service;
      const tabData = rootState.tab.tabData;
      if (!tabData || !service?.id) return;

      // Handle the case where project was migrated from CONNECT to ProjectWise 365.
      // We need to use default route from URL template to have a working tab reconfiguration
      const templateTokens = service.urlTemplate.split('/');
      if (templateTokens.length > 2) {
        const defaultRoute = templateTokens.pop();
        this.setService({ ...service, urlTemplate: templateTokens.join('/') });
        this.setDefaultRoute(defaultRoute);
      } else {
        this.setDefaultRoute(tabData.defaultRoute ?? '');
      }

      dispatch.connections.setConnection(undefined);
      await dispatch.connections.loadTabIsDefault({});
    },
  }),
});
